import { KeyboardEvent, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';
import { ChatMessage } from 'src/services/ai/ai.types';
import { usePostAiChatMutation } from 'src/services/ai/ai.service';

const PANEL_WIDTH = 380;
const TAB_WIDTH = 36;

export default function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const [postChat, { isLoading }] = usePostAiChatMutation();

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    scrollToBottom();

    try {
      const result = await postChat({ messages: next, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }).unwrap();
      setMessages([...next, { role: 'assistant', content: result.response }]);
      scrollToBottom();
    } catch {
      setMessages([
        ...next,
        { role: 'assistant', content: 'Something went wrong. Please try again.' },
      ]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        height: '100vh',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        pointerEvents: 'none',
      }}
    >
      {/* Slide-out panel */}
      <Box
        sx={{
          width: open ? PANEL_WIDTH : 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderLeft: `1px solid ${theme.palette.divider}`,
          boxShadow: open ? theme.customShadows?.z20 : 'none',
          pointerEvents: 'auto',
        }}
      >
        <Box sx={{ width: PANEL_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, bgcolor: '#078DEE', color: '#fff', flexShrink: 0 }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Box component="img" src="/assets/ai/linki-headshot.svg" sx={{ width: 28, height: 28, borderRadius: '50%' }} />
              <Typography variant="subtitle2">Linki</Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              {messages.length > 0 && (
                <IconButton size="small" onClick={() => setMessages([])} sx={{ color: 'inherit' }} title="New conversation">
                  <Iconify icon="solar:eraser-bold" width={18} />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
                <Iconify icon="solar:alt-arrow-right-bold" width={18} />
              </IconButton>
            </Stack>
          </Stack>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {messages.length === 0 && (
              <Stack gap={2} mt={2}>
                <Stack alignItems="center" gap={1} mb={1}>
                  <Box component="img" src="/assets/ai/linki-headshot.svg" sx={{ width: 56, height: 56 }} />
                  <Typography variant="subtitle1" fontWeight={700} textAlign="center">
                    Hi, I&apos;m Linki
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    I can brief you on your pipeline, highlight priorities, and take action on your behalf.
                  </Typography>
                </Stack>

                {[
                  { icon: 'solar:rocket-bold', label: 'Brief me on today' },
                  { icon: 'solar:phone-calling-bold', label: 'Who should I call today?' },
                  { icon: 'solar:checklist-minimalistic-bold', label: 'What are my upcoming tasks?' },
                  { icon: 'solar:dollar-bold', label: "What's in my deal pipeline?" },
                  { icon: 'solar:letter-bold', label: 'Draft outreach for a candidate' },
                  { icon: 'solar:graph-bold', label: 'Pipeline health check' },
                ].map(({ icon, label }) => (
                  <Box
                    key={label}
                    onClick={() => { setInput(label); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.25,
                      borderRadius: 1.5,
                      border: `1px solid ${theme.palette.divider}`,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#2DD3BE18' },
                      transition: theme.transitions.create('background-color'),
                    }}
                  >
                    <Iconify icon={icon} width={20} sx={{ color: '#6466F1', flexShrink: 0 }} />
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: msg.role === 'user' ? '#005AD2' : 'background.neutral',
                    color: msg.role === 'user' ? '#fff' : 'text.primary',
                    '& p': { m: 0 },
                    '& ul, & ol': { pl: 2, m: 0 },
                    '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 12 },
                    '& th, & td': { border: '1px solid', p: 0.5 },
                    '& h2, & h3': { mt: 1, mb: 0.5, fontSize: 14 },
                    '& blockquote': { borderLeft: '3px solid', pl: 1, ml: 0, opacity: 0.8 },
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <Typography variant="body2" component="div">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </Typography>
                  ) : (
                    <Typography variant="body2">{msg.content}</Typography>
                  )}
                </Box>
              </Box>
            ))}

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: 'background.neutral' }}>
                  <CircularProgress size={16} />
                </Box>
              </Box>
            )}

            <div ref={bottomRef} />
          </Box>

          {/* Input */}
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
          >
            <InputBase
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ fontSize: 14 }}
            />
            <IconButton
              onClick={handleSend}
              sx={{ color: '#005AD2' }}
              disabled={!input.trim() || isLoading}
              size="small"
            >
              <Iconify icon="solar:plain-bold" width={20} />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Vertical tab handle */}
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          width: TAB_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          pointerEvents: 'auto',
          bgcolor: '#078DEE',
          color: '#fff',
          gap: 1,
          py: 2,
          alignSelf: 'center',
          borderRadius: '6px 0 0 6px',
          boxShadow: theme.customShadows?.z8,
          '&:hover': { bgcolor: '#0351AB' },
          transition: theme.transitions.create('background-color'),
        }}
      >
        <Box component="img" src="/assets/ai/linki-headshot.svg" sx={{ width: 24, height: 24, borderRadius: '50%' }} />
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            letterSpacing: 0.5,
            fontSize: 11,
          }}
        >
          Linki
        </Typography>
      </Box>
    </Box>
  );
}
